/**
 * In-memory stand-in for a FileSystemWritableFileStream.
 *
 * Two upstream writers only know how to write to the File System Access API:
 * the document saver (BrowserFileSystem → `seek` / `write` / `truncate` /
 * `close` called directly on the stream) and the video renderer (mediabunny's
 * StreamTarget → `getWriter()` and positioned `{ type: 'write' }` chunks).
 * This class satisfies both by extending WritableStream for the writer path
 * and exposing the direct methods for the other, and collects everything in
 * one growable buffer — so a `.ssproj` or a preview video can be produced as
 * bytes for an upload without ever touching disk.
 */

type WriteChunk =
    | BufferSource
    | Blob
    | string
    | { type: 'write'; data: BufferSource | Blob | string; position?: number }
    | { type: 'seek'; position: number }
    | { type: 'truncate'; size: number };

const toBytes = async (data: BufferSource | Blob | string): Promise<Uint8Array> => {
    if (typeof data === 'string') return new TextEncoder().encode(data);
    if (data instanceof Blob) return new Uint8Array(await data.arrayBuffer());
    if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return new Uint8Array(data);
};

class MemoryStore {
    private buffer = new Uint8Array(1 << 20);
    private length = 0;
    private cursor = 0;
    closed = false;

    private ensure(size: number) {
        if (size <= this.buffer.byteLength) return;
        let next = this.buffer.byteLength * 2;
        while (next < size) next *= 2;
        const grown = new Uint8Array(next);
        grown.set(this.buffer.subarray(0, this.length));
        this.buffer = grown;
    }

    async seek(position: number): Promise<void> {
        this.cursor = Math.max(0, position);
    }

    async truncate(size: number): Promise<void> {
        this.ensure(size);
        if (size > this.length) this.buffer.fill(0, this.length, size);
        this.length = size;
        if (this.cursor > size) this.cursor = size;
    }

    async write(chunk: WriteChunk): Promise<void> {
        if (chunk && typeof chunk === 'object' && 'type' in chunk && typeof (chunk as { type: unknown }).type === 'string') {
            const c = chunk as Exclude<WriteChunk, BufferSource | Blob | string>;
            if (c.type === 'seek') { await this.seek(c.position); return; }
            if (c.type === 'truncate') { await this.truncate(c.size); return; }
            if (c.position !== undefined) this.cursor = c.position;
            this.put(await toBytes(c.data));
            return;
        }
        this.put(await toBytes(chunk as BufferSource | Blob | string));
    }

    private put(bytes: Uint8Array) {
        const end = this.cursor + bytes.byteLength;
        this.ensure(end);
        if (this.cursor > this.length) this.buffer.fill(0, this.length, this.cursor);
        this.buffer.set(bytes, this.cursor);
        this.cursor = end;
        if (end > this.length) this.length = end;
    }

    async close(): Promise<void> {
        this.closed = true;
    }

    async abort(): Promise<void> {
        this.closed = true;
    }

    /** The bytes written so far (a view — copy it if the store lives on). */
    get bytes(): Uint8Array {
        return this.buffer.subarray(0, this.length);
    }
}

export class MemoryWritableStream extends WritableStream<WriteChunk> {
    private store: MemoryStore;

    constructor() {
        const store = new MemoryStore();
        super({
            write: chunk => store.write(chunk),
            close: () => store.close(),
            abort: () => store.abort()
        });
        this.store = store;
    }

    seek(position: number): Promise<void> {
        return this.store.seek(position);
    }

    write(chunk: WriteChunk): Promise<void> {
        return this.store.write(chunk);
    }

    truncate(size: number): Promise<void> {
        return this.store.truncate(size);
    }

    close(): Promise<void> {
        return this.store.close();
    }

    abort(): Promise<void> {
        return this.store.abort();
    }

    /** A copy of everything written. */
    get bytes(): Uint8Array {
        return this.store.bytes.slice();
    }
}
